import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  FileText, ChevronRight, ChevronLeft, Check, Building, 
  Target, Users, Calendar, BookOpen, Shield, Award,
  AlertTriangle, Clock, RefreshCw, Download, Loader2,
  Sparkles, HelpCircle, Lightbulb, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FormattedContent } from '../components/FormattedContent';
import { AIInlineSuggestion } from '../components/WizardComponents';
import WizardShell from '../components/WizardShell';
import { getToken } from '../utils/tokenStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Field tooltip component
function FieldTooltip({ content }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative inline-block ml-1">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="text-slate-400 hover:text-sky-700 transition-colors"
      >
        <HelpCircle className="w-4 h-4" />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute z-50 w-64 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-lg bottom-6 left-0">
            <div className="absolute w-2 h-2 bg-slate-900 transform rotate-45 -bottom-1 left-2" />
            {content}
          </div>
        </>
      )}
    </div>
  );
}

export default function BCMPolicyWizardPage() {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [wizardState, setWizardState] = useState(null);
  const [currentPhase, setCurrentPhase] = useState('foundation');
  const [questions, setQuestions] = useState(null);
  const [documents, setDocuments] = useState(null);
  
  // AI Draft state
  const [aiDraftLoading, setAiDraftLoading] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  
  // Foundation step tracking (sub-steps within Phase 1)
  // 0: Organization, 1: Policy Statement, 2: Scope, 3: Governance, 4: Metadata
  const [foundationStep, setFoundationStep] = useState(0);
  
  // Foundation form state
  const [foundation, setFoundation] = useState({
    mission: '',
    vision: '',
    core_values: [],
    bcm_objectives: [],
    policy_owner: '',
    executive_sponsor: ''
  });
  const [newValue, setNewValue] = useState('');
  
  // Policy Statement state (Step 1)
  const [policyStatement, setPolicyStatement] = useState({
    title: 'Business Continuity Management Policy',
    purpose: '',
    commitment: '',
    applicability: ''
  });
  
  // Scope Management state (Step 2)
  const [scopeManagement, setScopeManagement] = useState({
    in_scope_departments: [],
    excluded_departments: [],
    in_scope_locations: [],
    excluded_locations: [],
    in_scope_processes: [],
    excluded_processes: [],
    scope_justification: '',
    exclusion_rationale: ''
  });
  const [newInScopeDept, setNewInScopeDept] = useState('');
  const [newExcludedDept, setNewExcludedDept] = useState('');
  
  // Governance Structure state (Step 3)
  const [governanceStructure, setGovernanceStructure] = useState({
    executive_sponsor: { name: '', title: '', responsibilities: '' },
    bcm_steering_committee: [],
    bcm_manager: { name: '', title: '', responsibilities: '' },
    department_coordinators: [],
    reporting_structure: '',
    meeting_frequency: 'quarterly',
    escalation_path: ''
  });
  
  // Policy Metadata state (Step 4)
  const [policyMetadata, setPolicyMetadata] = useState({
    version: '1.0',
    effective_date: '',
    review_date: '',
    next_review_date: '',
    document_owner: '',
    approved_by: '',
    approval_date: '',
    distribution: 'internal',
    classification: 'confidential',
    change_history: []
  });
  
  // Organization data for dropdowns (Task 4)
  const [executives, setExecutives] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Organization Mission & Vision (surfaced from the org profile / onboarding enrichment)
  const [orgFoundation, setOrgFoundation] = useState({ mission: '', vision: '' });
  
  // Maintenance form state
  const [maintenance, setMaintenance] = useState({
    bia_frequency: 'annually',
    ra_frequency: 'annually',
    plan_review_interval: 'bi-annually',
    tabletop_frequency: 'quarterly',
    component_test_frequency: 'bi-annually',
    full_simulation_frequency: 'annually',
    level1_training_frequency: 'annually',
    level2_training_frequency: 'bi-annually',
    level3_training_frequency: 'annually'
  });

  const fetchWizardState = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/bia-engine/policy/wizard/state`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      
      setWizardState(data);
      setCurrentPhase(data.phase);
      setQuestions(data.interview_questions);
      
      if (data.foundation) {
        setFoundation({
          mission: data.foundation.mission || '',
          vision: data.foundation.vision || '',
          core_values: data.foundation.core_values || [],
          bcm_objectives: data.foundation.bcm_objectives || [],
          policy_owner: data.foundation.policy_owner || '',
          executive_sponsor: data.foundation.executive_sponsor || ''
        });
      }
      
      if (data.maintenance) {
        setMaintenance(data.maintenance);
      }
      
      // If completed, fetch documents
      if (data.phase === 'complete') {
        await fetchDocuments();
      }
    } catch (err) {
      toast.error('Failed to load wizard state');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/bia-engine/policy/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDocuments(data);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  };

  useEffect(() => {
    fetchWizardState();
    fetchOrgData();
  }, [fetchWizardState]);

  // Fetch executives and departments for governance dropdowns (Task 4)
  const fetchOrgData = async () => {
    try {
      const token = getToken();
      const [execRes, deptRes, orgRes] = await Promise.all([
        fetch(`${API_URL}/api/bcm-admin/executives`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/bcm-admin/departments`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/bcm-admin/organization`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (execRes.ok) {
        const execData = await execRes.json();
        setExecutives(execData || []);
      }
      if (deptRes.ok) {
        const deptData = await deptRes.json();
        setDepartments(deptData || []);
      }
      if (orgRes.ok) {
        const org = await orgRes.json();
        setOrgFoundation({ mission: org.mission_statement || '', vision: org.vision || '' });
      }
    } catch (err) {
      console.error('Error fetching org data:', err);
    }
  };

  // AI Policy Draft Generator
  const handleAIGenerateDraft = async () => {
    setAiDraftLoading(true);
    try {
      const token = getToken();
      const userStr = localStorage.getItem('gbrms_user');
      const user = userStr ? JSON.parse(userStr) : {};
      
      // Fetch organization data for context
      const orgRes = await fetch(`${API_URL}/api/bcm-admin/organization`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => null);
      const orgData = orgRes?.ok ? await orgRes.json() : {};
      
      // Fetch departments for scope suggestions
      const deptRes = await fetch(`${API_URL}/api/bcm-admin/departments`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => null);
      const departments = deptRes?.ok ? await deptRes.json() : [];
      
      // Fetch locations
      const locRes = await fetch(`${API_URL}/api/bcm-admin/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => null);
      const locations = (locRes?.ok ? await locRes.json() : []).filter(l => l.status !== 'retired');
      
      // Generate policy draft
      const res = await fetch(`${API_URL}/api/ai-assistant/generate-policy-draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          org_id: user.org_id || '',
          org_name: orgData.name || user.org_name || 'Organization',
          industry: orgData.industry || user.industry || 'general',
          headquarters_address: locations[0]?.address || '',
          departments: departments.map(d => d.name),
          draft_type: 'full'
        })
      });
      
      const data = await res.json();
      if (data.success) {
        setAiDraft(data);
        toast.success('AI Policy Draft generated! All sections populated.');
        
        // 1. Auto-fill Policy Statement
        if (data.policy_draft?.statement) {
          const statementText = data.policy_draft.statement;
          setPolicyStatement(prev => ({
            ...prev,
            purpose: `This policy establishes the framework for Business Continuity Management at ${orgData.name || 'the organization'}, ensuring the organization can respond to and recover from disruptive incidents while maintaining critical business operations.`,
            commitment: statementText.substring(0, 500) || `${orgData.name || 'The organization'} is committed to maintaining business continuity capabilities that protect our stakeholders, employees, and operations from the impact of disruptive events.`,
            applicability: `This policy applies to all business units, departments, employees, contractors, and third-party service providers operating within or on behalf of ${orgData.name || 'the organization'}.`
          }));
        }
        
        // 2. Auto-fill Scope Management based on departments
        if (departments.length > 0) {
          // Get scope suggestions from AI
          const scopeRes = await fetch(`${API_URL}/api/ai-assistant/analyze-scope`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              org_id: user.org_id || '',
              industry: orgData.industry || 'general',
              departments: departments.map(d => d.name)
            })
          }).catch(() => null);
          
          const scopeData = scopeRes?.ok ? await scopeRes.json() : null;
          if (scopeData?.success) {
            setScopeManagement(prev => ({
              ...prev,
              in_scope_departments: scopeData.in_scope.map(d => d.name),
              excluded_departments: scopeData.excluded.map(d => d.name),
              in_scope_locations: locations.map(l => l.name || l.address),
              scope_justification: scopeData.recommendation || 'Based on industry standards and criticality assessment.',
              exclusion_rationale: 'Lower criticality departments will be phased into the BCM program in subsequent iterations.'
            }));
          }
        }
        
        // 3. Auto-fill Governance Structure
        setGovernanceStructure(prev => ({
          ...prev,
          executive_sponsor: {
            name: foundation.executive_sponsor || '[To be assigned]',
            title: 'Chief Executive Officer / Chief Operating Officer',
            responsibilities: 'Provide executive oversight, approve BCM policy, allocate resources, champion BCM culture'
          },
          bcm_manager: {
            name: foundation.policy_owner || '[To be assigned]',
            title: 'BCM Manager / Business Continuity Lead',
            responsibilities: 'Day-to-day BCM program management, coordinate exercises, maintain plans, report to steering committee'
          },
          reporting_structure: 'BCM Manager reports to Executive Sponsor with quarterly updates to the BCM Steering Committee',
          meeting_frequency: 'quarterly',
          escalation_path: '1. Department Coordinator → 2. BCM Manager → 3. Steering Committee → 4. Executive Sponsor'
        }));
        
        // 4. Auto-fill Policy Metadata
        const today = new Date();
        const reviewDate = new Date(today);
        reviewDate.setFullYear(reviewDate.getFullYear() + 1);
        
        setPolicyMetadata(prev => ({
          ...prev,
          version: '1.0',
          effective_date: today.toISOString().split('T')[0],
          next_review_date: reviewDate.toISOString().split('T')[0],
          document_owner: foundation.policy_owner || '[To be assigned]',
          approved_by: foundation.executive_sponsor || '[To be assigned]',
          distribution: 'internal',
          classification: 'confidential'
        }));
        
        // 5. Auto-fill Foundation fields
        if (data.policy_draft?.statement) {
          const lines = data.policy_draft.statement.split('\n').filter(l => l.trim());
          const firstContent = lines.find(l => !l.includes('POLICY') && l.length > 50);
          if (firstContent && !foundation.mission) {
            setFoundation(prev => ({
              ...prev,
              mission: firstContent.trim().substring(0, 500)
            }));
          }
        }
        
        if (data.policy_draft?.objectives?.length > 0 && foundation.bcm_objectives.length === 0) {
          const mappedObjectives = data.policy_draft.objectives.slice(0, 4).map(obj => {
            if (obj.toLowerCase().includes('life') || obj.toLowerCase().includes('safety')) {
              return 'Protect Human Life and Safety';
            }
            if (obj.toLowerCase().includes('trust') || obj.toLowerCase().includes('customer')) {
              return 'Maintain Customer Trust and Confidence';
            }
            if (obj.toLowerCase().includes('regulatory') || obj.toLowerCase().includes('compliance')) {
              return 'Meet Regulatory and Compliance Requirements';
            }
            if (obj.toLowerCase().includes('financial')) {
              return 'Minimize Financial Impact from Disruptions';
            }
            if (obj.toLowerCase().includes('reputation')) {
              return 'Protect Organizational Reputation';
            }
            if (obj.toLowerCase().includes('operations') || obj.toLowerCase().includes('critical')) {
              return 'Ensure Continuity of Critical Operations';
            }
            return null;
          }).filter(Boolean);
          
          if (mappedObjectives.length > 0) {
            setFoundation(prev => ({
              ...prev,
              bcm_objectives: [...new Set([...prev.bcm_objectives, ...mappedObjectives])]
            }));
          }
        }
      } else {
        toast.error(data.error || 'Failed to generate policy draft');
      }
    } catch (err) {
      console.error('AI draft generation error:', err);
      toast.error('Failed to generate AI policy draft');
    } finally {
      setAiDraftLoading(false);
    }
  };

  const handleSaveFoundation = async () => {
    // Mission/Vision/Core Values are now managed in Organization Management page
    // Only require Policy Owner and Executive Sponsor
    if (!foundation.policy_owner || !foundation.executive_sponsor) {
      toast.error('Please fill in Policy Owner and Executive Sponsor');
      return;
    }
    
    // Validate policy statement fields
    if (!policyStatement.purpose || !policyStatement.commitment) {
      toast.error('Please fill in Purpose and Management Commitment');
      return;
    }
    
    setGenerating(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/bia-engine/policy/wizard/foundation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...foundation,
          policy_statement: policyStatement
        })
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success('Foundation phase complete!');
        setCurrentPhase('maintenance');
        setQuestions(data.interview_questions);
      } else {
        toast.error(data.detail || 'Failed to save foundation');
      }
    } catch (err) {
      toast.error('Failed to save foundation');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveMaintenance = async () => {
    setGenerating(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/bia-engine/policy/wizard/maintenance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(maintenance)
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success('Maintenance phase complete!');
        setCurrentPhase('complete');
        // Auto-generate documents
        await handleGenerateDocuments();
      } else {
        toast.error(data.detail || 'Failed to save maintenance');
      }
    } catch (err) {
      toast.error('Failed to save maintenance');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateDocuments = async () => {
    setGenerating(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/bia-engine/policy/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success('Policy documents generated!');
        setDocuments(data);
      } else {
        toast.error(data.detail || 'Failed to generate documents');
      }
    } catch (err) {
      toast.error('Failed to generate documents');
    } finally {
      setGenerating(false);
    }
  };

  const handleResetWizard = async () => {
    if (!window.confirm('Are you sure you want to reset the wizard? All progress will be lost.')) {
      return;
    }
    
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/bia-engine/policy/wizard/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Wizard reset');
        setFoundation({
          mission: '',
          vision: '',
          core_values: [],
          bcm_objectives: [],
          policy_owner: '',
          executive_sponsor: ''
        });
        setDocuments(null);
        await fetchWizardState();
      }
    } catch (err) {
      toast.error('Failed to reset wizard');
    }
  };

  const addCoreValue = () => {
    if (newValue.trim() && !foundation.core_values.includes(newValue.trim())) {
      setFoundation(prev => ({
        ...prev,
        core_values: [...prev.core_values, newValue.trim()]
      }));
      setNewValue('');
    }
  };

  const removeCoreValue = (value) => {
    setFoundation(prev => ({
      ...prev,
      core_values: prev.core_values.filter(v => v !== value)
    }));
  };

  // Scope Department Management Functions
  const addInScopeDepartment = () => {
    if (newInScopeDept.trim() && !scopeManagement.in_scope_departments.includes(newInScopeDept.trim())) {
      setScopeManagement(prev => ({
        ...prev,
        in_scope_departments: [...prev.in_scope_departments, newInScopeDept.trim()]
      }));
      setNewInScopeDept('');
    }
  };

  const removeInScopeDepartment = (dept) => {
    setScopeManagement(prev => ({
      ...prev,
      in_scope_departments: prev.in_scope_departments.filter(d => d !== dept)
    }));
  };

  const addExcludedDepartment = () => {
    if (newExcludedDept.trim() && !scopeManagement.excluded_departments.includes(newExcludedDept.trim())) {
      setScopeManagement(prev => ({
        ...prev,
        excluded_departments: [...prev.excluded_departments, newExcludedDept.trim()]
      }));
      setNewExcludedDept('');
    }
  };

  const removeExcludedDepartment = (dept) => {
    setScopeManagement(prev => ({
      ...prev,
      excluded_departments: prev.excluded_departments.filter(d => d !== dept)
    }));
  };

  const toggleObjective = (obj) => {
    setFoundation(prev => ({
      ...prev,
      bcm_objectives: prev.bcm_objectives.includes(obj)
        ? prev.bcm_objectives.filter(o => o !== obj)
        : [...prev.bcm_objectives, obj]
    }));
  };

  const objectives = [
    { id: 'protect_life', label: 'Protect Human Life and Safety', recommended: true },
    { id: 'maintain_trust', label: 'Maintain Customer Trust and Confidence', recommended: true },
    { id: 'regulatory', label: 'Meet Regulatory and Compliance Requirements', recommended: true },
    { id: 'financial', label: 'Minimize Financial Impact from Disruptions' },
    { id: 'reputation', label: 'Protect Organizational Reputation' },
    { id: 'operations', label: 'Ensure Continuity of Critical Operations' },
    { id: 'supply_chain', label: 'Maintain Supply Chain Resilience' },
    { id: 'stakeholder', label: 'Fulfill Stakeholder Obligations' }
  ];

  const frequencyOptions = [
    { value: 'quarterly', label: 'Quarterly', desc: 'Every 3 months' },
    { value: 'bi-annually', label: 'Bi-Annually', desc: 'Every 6 months' },
    { value: 'annually', label: 'Annually', desc: 'Every 12 months' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="bcm-policy-loading">
        <Loader2 className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  const progress = currentPhase === 'foundation' ? 33 : currentPhase === 'maintenance' ? 66 : 100;

  const POLICY_STEPS = [
    { title: 'Policy Foundation', subtitle: 'Establish the "Why" of your BCM program', phase: 'foundation', isComplete: currentPhase === 'maintenance' || currentPhase === 'complete' },
    { title: 'Program Governance', subtitle: 'Define maintenance and review schedules', phase: 'maintenance', isComplete: currentPhase === 'complete' },
    { title: 'Generate Documents', subtitle: 'Review and approve generated policies', phase: 'complete', isComplete: false },
  ];

  const POLICY_CONTEXT_HELP = {
    foundation: { title: "Policy Foundation", sections: [
      { heading: "About This Phase", content: "Establish the foundation of your BCM Program by aligning it with your organization's mission, goals, and compliance requirements. The AI can generate a draft based on your org details." },
      { heading: "Best Practice", content: "Focus on the 'Why' — articulate business justification, scope boundaries, and executive commitment. Reference your organization's risk appetite and regulatory landscape." },
    ], isoClause: "ISO 22301 - Clause 5.2" },
    maintenance: { title: "Program Governance", sections: [
      { heading: "About This Phase", content: "Define how your BCM program will be maintained over time — review frequencies, exercise schedules, audit triggers, and continuous improvement cycles." },
      { heading: "Best Practice", content: "Annual policy reviews are the minimum. Quarterly reviews for high-risk sectors. Define clear triggers for ad-hoc reviews (e.g., major incidents, org restructuring)." },
    ], isoClause: "ISO 22301 - Clause 9.3" },
    complete: { title: "Document Generation", sections: [
      { heading: "About This Phase", content: "Review the AI-generated BCM Policy documents. You can edit, regenerate, or approve them. Approved documents become your official BCM Policy foundation." },
      { heading: "Best Practice", content: "Have the Crisis Director or BCM Sponsor review the policy before final approval. Ensure alignment with your organization's existing governance framework." },
    ], isoClause: "ISO 22301 - Clause 7.5" },
  };

  const phaseIndex = currentPhase === 'foundation' ? 0 : currentPhase === 'maintenance' ? 1 : 2;

  return (
    <WizardShell
      title="BCM Policy Wizard"
      steps={POLICY_STEPS}
      currentStep={phaseIndex}
      onStepChange={(idx) => {
        const phases = ['foundation', 'maintenance', 'complete'];
        const targetPhase = phases[idx];
        const canNavigate = idx === 0 || POLICY_STEPS[idx - 1]?.isComplete;
        if (canNavigate) setCurrentPhase(targetPhase);
      }}
      completionPct={progress}
      contextHelp={POLICY_CONTEXT_HELP[currentPhase]}
      onBack={phaseIndex > 0 ? () => {
        const phases = ['foundation', 'maintenance', 'complete'];
        setCurrentPhase(phases[phaseIndex - 1]);
      } : null}
      onSave={handleResetWizard}
    >
      <div data-testid="bcm-policy-wizard-page">

      {/* Phase 1: Foundation */}
      {currentPhase === 'foundation' && (
        <Card data-testid="foundation-phase">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="w-5 h-5 text-sky-700" />
              Phase 1: Policy Foundation (The &quot;Why&quot;)
            </CardTitle>
            <CardDescription>
              Let&apos;s establish the foundation of your BCM Program by aligning it with your organization&apos;s mission and goals.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* AI Generate Draft Button */}
            <div className="p-4 bg-gradient-to-r from-violet-50 via-purple-50 to-indigo-50 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">AI Policy Draft Generator</p>
                    <p className="text-sm text-slate-600">Auto-generate policy content based on your organization</p>
                  </div>
                </div>
                <Button 
                  onClick={handleAIGenerateDraft}
                  disabled={aiDraftLoading}
                  className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                  data-testid="ai-generate-draft-btn"
                >
                  {aiDraftLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Draft
                    </>
                  )}
                </Button>
              </div>
              {aiDraft && (
                <div className="mt-4 p-3 bg-white rounded-lg border border-purple-100">
                  <p className="text-xs font-medium text-purple-700 mb-2">AI-Generated Policy Statement Preview:</p>
                  <p className="text-sm text-slate-700 line-clamp-3">{aiDraft.policy_draft?.statement?.substring(0, 300) || 'Draft generated. Review and customize below.'}...</p>
                  {aiDraft.regulatory_context?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {aiDraft.regulatory_context.map((reg, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs bg-purple-50">{reg}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sub-step Navigation for Phase 1 */}
            <div className="flex items-center gap-1 p-3 bg-slate-50 rounded-lg overflow-x-auto">
              {[
                { step: 0, label: 'BCM Policy Statement', icon: FileText },
                { step: 1, label: 'BCM Scope Management', icon: Target },
                { step: 2, label: 'BCM Governance Structure', icon: Users },
                { step: 3, label: 'BCM Policy Metadata', icon: Info }
              ].map((item, idx) => {
                const Icon = item.icon;
                const isActive = foundationStep === item.step;
                const isComplete = foundationStep > item.step;
                return (
                  <React.Fragment key={item.step}>
                    <button
                      onClick={() => setFoundationStep(item.step)}
                      className={`flex items-center gap-2 px-2 sm:px-3 py-2 rounded-lg transition-all whitespace-nowrap ${
                        isActive ? 'bg-sky-700 text-white' :
                        isComplete ? 'bg-emerald-100 text-emerald-700' :
                        'bg-white text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        isActive ? 'bg-white/20' : isComplete ? 'bg-emerald-200' : 'bg-slate-200'
                      }`}>
                        {isComplete ? <Check className="w-3 h-3" /> : item.step + 1}
                      </div>
                      <span className="text-sm font-medium hidden sm:inline">{item.label}</span>
                    </button>
                    {idx < 3 && <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Step 0: BCM Policy Statement (formerly Step 1, now includes BCM Program Objectives) */}
            {foundationStep === 0 && (
              <div className="space-y-4 p-4 border rounded-lg bg-white" data-testid="step-policy-statement">
                <div className="flex items-center gap-2 pb-3 border-b">
                  <FileText className="w-5 h-5 text-sky-700" />
                  <h3 className="font-semibold text-lg">Step 1: BCM Policy Statement</h3>
                </div>
                
                <AIInlineSuggestion
                  type="tip"
                  suggestion="The BCM Policy Statement establishes the framework and commitment for business continuity. Organization Mission/Vision/Core Values are now managed in the Organization Management page."
                />

                {(orgFoundation.mission || orgFoundation.vision) && (
                  <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-4 space-y-3" data-testid="policy-mission-vision-card">
                    <div className="flex items-center gap-2 text-indigo-800">
                      <Target className="w-4 h-4" />
                      <span className="text-sm font-semibold">Organization Mission &amp; Vision</span>
                      <Badge variant="outline" className="text-[10px] bg-white text-indigo-700 border-indigo-200">Pre-filled from Organization Profile</Badge>
                    </div>
                    {orgFoundation.mission && (
                      <div data-testid="policy-mission-text">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500 mb-0.5">Mission</p>
                        <p className="text-sm text-slate-700">{orgFoundation.mission}</p>
                      </div>
                    )}
                    {orgFoundation.vision && (
                      <div data-testid="policy-vision-text">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500 mb-0.5">Vision</p>
                        <p className="text-sm text-slate-700">{orgFoundation.vision}</p>
                      </div>
                    )}
                    <p className="text-[11px] text-indigo-500">Use these as context when drafting the policy purpose and commitment. Edit them anytime in Organization Management.</p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="policy_title">Policy Title</Label>
                  <Input
                    id="policy_title"
                    value={policyStatement.title}
                    onChange={e => setPolicyStatement(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Business Continuity Management Policy"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="policy_purpose">Purpose <span className="text-red-500">*</span></Label>
                  <Textarea
                    id="policy_purpose"
                    value={policyStatement.purpose}
                    onChange={e => setPolicyStatement(prev => ({ ...prev, purpose: e.target.value }))}
                    placeholder="This policy establishes the framework for Business Continuity Management..."
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="policy_commitment">Management Commitment <span className="text-red-500">*</span></Label>
                  <Textarea
                    id="policy_commitment"
                    value={policyStatement.commitment}
                    onChange={e => setPolicyStatement(prev => ({ ...prev, commitment: e.target.value }))}
                    placeholder="[Organization] is committed to maintaining business continuity capabilities..."
                    rows={4}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="policy_applicability">Applicability</Label>
                  <Textarea
                    id="policy_applicability"
                    value={policyStatement.applicability}
                    onChange={e => setPolicyStatement(prev => ({ ...prev, applicability: e.target.value }))}
                    placeholder="This policy applies to all business units, employees, contractors..."
                    rows={2}
                  />
                </div>
                
                {/* BCM Program Objectives - moved from Organization tab */}
                <div className="space-y-3 pt-4 border-t">
                  <Label className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-sky-700" />
                    BCM Program Objectives
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {objectives.map(obj => (
                      <div
                        key={obj.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          foundation.bcm_objectives.includes(obj.label)
                            ? 'border-sky-700 bg-sky-50'
                            : 'border-slate-200 hover:border-sky-300'
                        }`}
                        onClick={() => toggleObjective(obj.label)}
                        data-testid={`objective-${obj.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={foundation.bcm_objectives.includes(obj.label)}
                            onChange={() => toggleObjective(obj.label)}
                          />
                          <div>
                            <span className="text-sm font-medium">{obj.label}</span>
                            {obj.recommended && (
                              <Badge variant="outline" className="ml-2 text-xs">Recommended</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Policy Owner & Executive Sponsor */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label htmlFor="policy_owner" className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-500" />
                      Policy Owner <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="policy_owner"
                      data-testid="policy-owner-input"
                      placeholder="Name and title (e.g., John Smith, BCM Manager)"
                      value={foundation.policy_owner}
                      onChange={e => setFoundation(prev => ({ ...prev, policy_owner: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="executive_sponsor" className="flex items-center gap-2">
                      <Award className="w-4 h-4 text-slate-500" />
                      Executive Sponsor <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="executive_sponsor"
                      data-testid="executive-sponsor-input"
                      placeholder="Name and title (e.g., Jane Doe, CRO)"
                      value={foundation.executive_sponsor}
                      onChange={e => setFoundation(prev => ({ ...prev, executive_sponsor: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: BCM Scope Management (formerly Step 2) */}
            {foundationStep === 1 && (
              <div className="space-y-4 p-4 border rounded-lg bg-white" data-testid="step-scope-management">
                <div className="flex items-center gap-2 pb-3 border-b">
                  <Target className="w-5 h-5 text-sky-700" />
                  <h3 className="font-semibold text-lg">Step 2: BCM Scope Management</h3>
                </div>
                
                <AIInlineSuggestion
                  type="tip"
                  suggestion="ISO 22301 requires clear definition of BCM scope. Include critical departments and key locations. Exclusions must be justified."
                />
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-600" />
                      In-Scope Departments
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add department..."
                        value={newInScopeDept}
                        onChange={e => setNewInScopeDept(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addInScopeDepartment())}
                        data-testid="in-scope-dept-input"
                      />
                      <Button type="button" onClick={addInScopeDepartment} variant="outline" size="sm">Add</Button>
                    </div>
                    <div className="p-3 border rounded-lg bg-emerald-50 min-h-[80px]">
                      {scopeManagement.in_scope_departments.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {scopeManagement.in_scope_departments.map((dept, idx) => (
                            <Badge key={idx} variant="outline" className="bg-emerald-100 text-emerald-700">
                              {dept}
                              <button onClick={() => removeInScopeDepartment(dept)} className="ml-1 hover:text-red-500">×</button>
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400">Add departments or click &quot;Generate All Sections&quot;</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      Excluded Departments
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add excluded department..."
                        value={newExcludedDept}
                        onChange={e => setNewExcludedDept(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addExcludedDepartment())}
                        data-testid="excluded-dept-input"
                      />
                      <Button type="button" onClick={addExcludedDepartment} variant="outline" size="sm">Add</Button>
                    </div>
                    <div className="p-3 border rounded-lg bg-amber-50 min-h-[80px]">
                      {scopeManagement.excluded_departments.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {scopeManagement.excluded_departments.map((dept, idx) => (
                            <Badge key={idx} variant="outline" className="bg-amber-100 text-amber-700">
                              {dept}
                              <button onClick={() => removeExcludedDepartment(dept)} className="ml-1 hover:text-red-500">×</button>
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400">No exclusions defined</p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="scope_justification">Scope Justification</Label>
                  <Textarea
                    id="scope_justification"
                    value={scopeManagement.scope_justification}
                    onChange={e => setScopeManagement(prev => ({ ...prev, scope_justification: e.target.value }))}
                    placeholder="Explain the rationale for the selected scope..."
                    rows={2}
                  />
                </div>
              </div>
            )}

            {/* Step 2: BCM Governance Structure (formerly Step 3) */}
            {foundationStep === 2 && (
              <div className="space-y-4 p-4 border rounded-lg bg-white" data-testid="step-governance-structure">
                <div className="flex items-center gap-2 pb-3 border-b">
                  <Users className="w-5 h-5 text-sky-700" />
                  <h3 className="font-semibold text-lg">Step 3: BCM Governance Structure</h3>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="border-sky-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Award className="w-4 h-4 text-sky-700" />
                        Executive Sponsor
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Select from Organization</Label>
                        <Select 
                          value={governanceStructure.executive_sponsor.name}
                          onValueChange={v => {
                            // Find the selected executive or team lead
                            const exec = executives.find(e => e.name === v);
                            const dept = departments.find(d => d.primary_owner_name === v || d.secondary_owner_name === v);
                            if (exec) {
                              setGovernanceStructure(prev => ({
                                ...prev, 
                                executive_sponsor: { 
                                  ...prev.executive_sponsor, 
                                  name: exec.name, 
                                  title: exec.designation || exec.role_type || ''
                                }
                              }));
                            } else if (dept) {
                              const isOwner = dept.primary_owner_name === v;
                              setGovernanceStructure(prev => ({
                                ...prev,
                                executive_sponsor: {
                                  ...prev.executive_sponsor,
                                  name: v,
                                  title: isOwner ? `Primary Owner - ${dept.name}` : `Secondary Owner - ${dept.name}`
                                }
                              }));
                            } else {
                              setGovernanceStructure(prev => ({
                                ...prev, executive_sponsor: { ...prev.executive_sponsor, name: v }
                              }));
                            }
                          }}
                        >
                          <SelectTrigger><SelectValue placeholder="Select Executive or Team Lead" /></SelectTrigger>
                          <SelectContent>
                            {executives.length > 0 && (
                              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Key Executives</div>
                            )}
                            {executives.map(exec => (
                              <SelectItem key={exec.id || exec.name} value={exec.name}>
                                {exec.name} - {exec.designation || exec.role_type}
                              </SelectItem>
                            ))}
                            {departments.some(d => d.primary_owner_name || d.secondary_owner_name) && (
                              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 mt-1">Team Leads</div>
                            )}
                            {departments.filter(d => d.primary_owner_name).map(dept => (
                              <SelectItem key={`primary-${dept.id}`} value={dept.primary_owner_name}>
                                {dept.primary_owner_name} - Primary ({dept.name})
                              </SelectItem>
                            ))}
                            {departments.filter(d => d.secondary_owner_name).map(dept => (
                              <SelectItem key={`secondary-${dept.id}`} value={dept.secondary_owner_name}>
                                {dept.secondary_owner_name} - Secondary ({dept.name})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Input placeholder="Title (e.g., CEO, COO)" value={governanceStructure.executive_sponsor.title}
                        onChange={e => setGovernanceStructure(prev => ({
                          ...prev, executive_sponsor: { ...prev.executive_sponsor, title: e.target.value }
                        }))} />
                      <Textarea placeholder="Key responsibilities..." rows={2}
                        value={governanceStructure.executive_sponsor.responsibilities}
                        onChange={e => setGovernanceStructure(prev => ({
                          ...prev, executive_sponsor: { ...prev.executive_sponsor, responsibilities: e.target.value }
                        }))} />
                    </CardContent>
                  </Card>
                  
                  <Card className="border-emerald-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Shield className="w-4 h-4 text-emerald-600" />
                        BCM Manager
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Select from Organization</Label>
                        <Select 
                          value={governanceStructure.bcm_manager.name}
                          onValueChange={v => {
                            const exec = executives.find(e => e.name === v);
                            const dept = departments.find(d => d.primary_owner_name === v || d.secondary_owner_name === v);
                            if (exec) {
                              setGovernanceStructure(prev => ({
                                ...prev, 
                                bcm_manager: { 
                                  ...prev.bcm_manager, 
                                  name: exec.name, 
                                  title: exec.designation || exec.role_type || ''
                                }
                              }));
                            } else if (dept) {
                              const isOwner = dept.primary_owner_name === v;
                              setGovernanceStructure(prev => ({
                                ...prev,
                                bcm_manager: {
                                  ...prev.bcm_manager,
                                  name: v,
                                  title: isOwner ? `Primary Owner - ${dept.name}` : `Secondary Owner - ${dept.name}`
                                }
                              }));
                            } else {
                              setGovernanceStructure(prev => ({
                                ...prev, bcm_manager: { ...prev.bcm_manager, name: v }
                              }));
                            }
                          }}
                        >
                          <SelectTrigger><SelectValue placeholder="Select Executive or Team Lead" /></SelectTrigger>
                          <SelectContent>
                            {executives.length > 0 && (
                              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Key Executives</div>
                            )}
                            {executives.map(exec => (
                              <SelectItem key={exec.id || exec.name} value={exec.name}>
                                {exec.name} - {exec.designation || exec.role_type}
                              </SelectItem>
                            ))}
                            {departments.some(d => d.primary_owner_name || d.secondary_owner_name) && (
                              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 mt-1">Team Leads</div>
                            )}
                            {departments.filter(d => d.primary_owner_name).map(dept => (
                              <SelectItem key={`primary-${dept.id}`} value={dept.primary_owner_name}>
                                {dept.primary_owner_name} - Primary ({dept.name})
                              </SelectItem>
                            ))}
                            {departments.filter(d => d.secondary_owner_name).map(dept => (
                              <SelectItem key={`secondary-${dept.id}`} value={dept.secondary_owner_name}>
                                {dept.secondary_owner_name} - Secondary ({dept.name})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Input placeholder="Title" value={governanceStructure.bcm_manager.title}
                        onChange={e => setGovernanceStructure(prev => ({
                          ...prev, bcm_manager: { ...prev.bcm_manager, title: e.target.value }
                        }))} />
                      <Textarea placeholder="Key responsibilities..." rows={2}
                        value={governanceStructure.bcm_manager.responsibilities}
                        onChange={e => setGovernanceStructure(prev => ({
                          ...prev, bcm_manager: { ...prev.bcm_manager, responsibilities: e.target.value }
                        }))} />
                    </CardContent>
                  </Card>
                </div>
                
                <div className="space-y-2">
                  <Label>Steering Committee Meeting Frequency</Label>
                  <Select value={governanceStructure.meeting_frequency}
                    onValueChange={v => setGovernanceStructure(prev => ({ ...prev, meeting_frequency: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="bi-weekly">Bi-Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="bi-annually">Bi-Annually</SelectItem>
                      <SelectItem value="annually">Annually</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 3: BCM Policy Metadata (formerly Step 4) */}
            {foundationStep === 3 && (
              <div className="space-y-4 p-4 border rounded-lg bg-white" data-testid="step-policy-metadata">
                <div className="flex items-center gap-2 pb-3 border-b">
                  <Info className="w-5 h-5 text-sky-700" />
                  <h3 className="font-semibold text-lg">Step 4: BCM Policy Metadata</h3>
                </div>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="version">Version</Label>
                    <Input id="version" value={policyMetadata.version}
                      onChange={e => setPolicyMetadata(prev => ({ ...prev, version: e.target.value }))} placeholder="1.0" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="effective_date">Effective Date</Label>
                    <Input id="effective_date" type="date" value={policyMetadata.effective_date}
                      onChange={e => setPolicyMetadata(prev => ({ ...prev, effective_date: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="next_review_date">Next Review Date</Label>
                    <Input id="next_review_date" type="date" value={policyMetadata.next_review_date}
                      onChange={e => setPolicyMetadata(prev => ({ ...prev, next_review_date: e.target.value }))} />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="document_owner">Document Owner</Label>
                    <Input id="document_owner" value={policyMetadata.document_owner}
                      onChange={e => setPolicyMetadata(prev => ({ ...prev, document_owner: e.target.value }))}
                      placeholder="BCM Manager name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="approved_by">Approved By</Label>
                    <Input id="approved_by" value={policyMetadata.approved_by}
                      onChange={e => setPolicyMetadata(prev => ({ ...prev, approved_by: e.target.value }))}
                      placeholder="Executive Sponsor name" />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Distribution</Label>
                    <Select value={policyMetadata.distribution}
                      onValueChange={v => setPolicyMetadata(prev => ({ ...prev, distribution: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="internal">Internal Only</SelectItem>
                        <SelectItem value="restricted">Restricted</SelectItem>
                        <SelectItem value="public">Public</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Classification</Label>
                    <Select value={policyMetadata.classification}
                      onValueChange={v => setPolicyMetadata(prev => ({ ...prev, classification: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="internal">Internal</SelectItem>
                        <SelectItem value="confidential">Confidential</SelectItem>
                        <SelectItem value="restricted">Restricted</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Sub-step Navigation Buttons */}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setFoundationStep(prev => Math.max(0, prev - 1))}
                disabled={foundationStep === 0}>
                <ChevronLeft className="w-4 h-4 mr-2" />Previous Step
              </Button>
              {foundationStep < 3 ? (
                <Button onClick={() => setFoundationStep(prev => prev + 1)}>
                  Next Step<ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={handleSaveFoundation} disabled={generating} data-testid="save-foundation-btn">
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Continue to Phase 2
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phase 2: Program Governance */}
      {currentPhase === 'maintenance' && (
        <Card data-testid="maintenance-phase">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-sky-700" />
              Phase 2: Program Governance (The &quot;How&quot;)
            </CardTitle>
            <CardDescription>
              Define the governance framework of your BCM Program - the regular activities that keep it alive and effective.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* AI Pro Tip */}
            <AIInlineSuggestion
              type="suggestion"
              suggestion="ISO 22301 recommends annual BIA and risk assessment reviews, with quarterly tabletop exercises for critical processes. The default frequencies below follow best practices."
              onApply={() => {
                setMaintenance({
                  bia_frequency: 'annually',
                  ra_frequency: 'annually',
                  plan_review_interval: 'bi-annually',
                  tabletop_frequency: 'quarterly',
                  component_test_frequency: 'bi-annually',
                  full_simulation_frequency: 'annually',
                  level1_training_frequency: 'annually',
                  level2_training_frequency: 'bi-annually',
                  level3_training_frequency: 'annually'
                });
                toast.success("Applied ISO 22301 recommended frequencies");
              }}
              actionLabel="Apply ISO 22301 Defaults"
            />

            {/* BIA & RA Frequency */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-500" />
                  BIA Refresh Frequency
                </Label>
                <Select
                  value={maintenance.bia_frequency}
                  onValueChange={val => setMaintenance(prev => ({ ...prev, bia_frequency: val }))}
                >
                  <SelectTrigger data-testid="bia-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {frequencyOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label} ({opt.desc})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">ISO 22301:2019 Clause 8.2.2</p>
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-slate-500" />
                  Risk Assessment Frequency
                </Label>
                <Select
                  value={maintenance.ra_frequency}
                  onValueChange={val => setMaintenance(prev => ({ ...prev, ra_frequency: val }))}
                >
                  <SelectTrigger data-testid="ra-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {frequencyOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">ISO 22301:2019 Clause 8.2.3</p>
              </div>
            </div>

            {/* Plan Review */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                Recovery Plan Review Interval
              </Label>
              <Select
                value={maintenance.plan_review_interval}
                onValueChange={val => setMaintenance(prev => ({ ...prev, plan_review_interval: val }))}
              >
                <SelectTrigger className="w-full md:w-1/2" data-testid="plan-review-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {frequencyOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">ISO 22301:2019 Clause 8.4.4</p>
            </div>

            {/* Exercise Strategy */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2 text-base font-semibold">
                <Shield className="w-4 h-4 text-sky-700" />
                Testing & Exercise Strategy
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { key: 'tabletop_frequency', label: 'Tabletop Exercises', desc: 'Discussion-based validation', recommended: 'quarterly' },
                  { key: 'component_test_frequency', label: 'Component Testing', desc: 'IT/Facility-specific tests', recommended: 'bi-annually' },
                  { key: 'full_simulation_frequency', label: 'Full Simulations', desc: 'Live rehearsal exercises', recommended: 'annually' }
                ].map(exercise => (
                  <Card key={exercise.key} className="border-slate-200">
                    <CardContent className="pt-4 space-y-3">
                      <div className="font-medium">{exercise.label}</div>
                      <p className="text-xs text-slate-500">{exercise.desc}</p>
                      <Select
                        value={maintenance[exercise.key]}
                        onValueChange={val => setMaintenance(prev => ({ ...prev, [exercise.key]: val }))}
                      >
                        <SelectTrigger data-testid={`${exercise.key}-select`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {frequencyOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label} {opt.value === exercise.recommended && '(Recommended)'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <p className="text-xs text-slate-500">ISO 22301:2019 Clause 8.5</p>
            </div>

            {/* Training Strategy */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2 text-base font-semibold">
                <BookOpen className="w-4 h-4 text-sky-700" />
                Training & Education Program
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { key: 'level1_training_frequency', label: 'Level 1: Awareness', audience: 'All Staff', recommended: 'annually' },
                  { key: 'level2_training_frequency', label: 'Level 2: Functional', audience: 'Process Owners & Recovery Teams', recommended: 'bi-annually' },
                  { key: 'level3_training_frequency', label: 'Level 3: Strategic', audience: 'EMT & Crisis Leaders', recommended: 'annually' }
                ].map(training => (
                  <Card key={training.key} className="border-slate-200">
                    <CardContent className="pt-4 space-y-3">
                      <div className="font-medium">{training.label}</div>
                      <p className="text-xs text-slate-500">{training.audience}</p>
                      <Select
                        value={maintenance[training.key]}
                        onValueChange={val => setMaintenance(prev => ({ ...prev, [training.key]: val }))}
                      >
                        <SelectTrigger data-testid={`${training.key}-select`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {frequencyOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label} {opt.value === training.recommended && '(Recommended)'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <p className="text-xs text-slate-500">ISO 22301:2019 Clause 7.2</p>
            </div>

            {/* Navigation */}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setCurrentPhase('foundation')}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back to Phase 1
              </Button>
              <Button onClick={handleSaveMaintenance} disabled={generating} data-testid="save-maintenance-btn">
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Generate Policy Documents
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phase 3: Documents */}
      {currentPhase === 'complete' && documents && (
        <div className="space-y-6" data-testid="complete-phase">
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="py-4 flex items-center gap-3">
              <Check className="w-6 h-6 text-emerald-600" />
              <div>
                <p className="font-medium text-emerald-800">Policy documents generated successfully!</p>
                <p className="text-sm text-emerald-600">Review and approve the documents below.</p>
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="policy" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="policy" data-testid="policy-tab">
                <FileText className="w-4 h-4 mr-2" />
                Policy Statement
              </TabsTrigger>
              <TabsTrigger value="calendar" data-testid="calendar-tab">
                <Calendar className="w-4 h-4 mr-2" />
                12-Month Calendar
              </TabsTrigger>
              <TabsTrigger value="dashboard" data-testid="dashboard-tab">
                <Shield className="w-4 h-4 mr-2" />
                Governance Dashboard
              </TabsTrigger>
            </TabsList>

            <TabsContent value="policy" className="mt-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>BCM Policy Statement</CardTitle>
                  <Badge variant={documents.status === 'approved' ? 'default' : 'secondary'}>
                    {documents.status === 'approved' ? 'Approved' : 'Draft'}
                  </Badge>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-50 p-6 rounded-lg overflow-auto max-h-[600px] border">
                    <FormattedContent content={documents.policy_statement} />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="calendar" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>12-Month Maintenance Calendar</CardTitle>
                  <CardDescription>
                    Fiscal Year: {documents.maintenance_calendar?.fiscal_year}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(documents.maintenance_calendar?.months || {}).map(([key, month]) => (
                      <Card key={key} className={month.activities.length > 0 ? 'border-sky-200' : ''}>
                        <CardHeader className="py-3">
                          <CardTitle className="text-sm">{month.name}</CardTitle>
                        </CardHeader>
                        <CardContent className="py-2">
                          {month.activities.length > 0 ? (
                            <ul className="space-y-1">
                              {month.activities.map((act, idx) => (
                                <li key={idx} className="text-xs flex items-start gap-2">
                                  <span className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${
                                    act.category === 'exercise' ? 'bg-sky-700' :
                                    act.category === 'training' ? 'bg-blue-500' :
                                    act.category === 'assessment' ? 'bg-emerald-500' :
                                    act.category === 'plans' ? 'bg-purple-500' : 'bg-slate-400'
                                  }`} />
                                  {act.name}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-slate-400">No scheduled activities</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  {/* Legend */}
                  <div className="flex gap-4 mt-4 pt-4 border-t">
                    {[
                      { color: 'bg-emerald-500', label: 'Assessment' },
                      { color: 'bg-purple-500', label: 'Plans' },
                      { color: 'bg-sky-700', label: 'Exercise' },
                      { color: 'bg-blue-500', label: 'Training' },
                      { color: 'bg-slate-400', label: 'Governance' }
                    ].map(item => (
                      <div key={item.label} className="flex items-center gap-2 text-xs">
                        <span className={`w-3 h-3 rounded-full ${item.color}`} />
                        {item.label}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="dashboard" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    Governance Dashboard
                    <Badge variant={
                      documents.governance_dashboard?.health_status === 'Good' ? 'default' :
                      documents.governance_dashboard?.health_status === 'Needs Attention' ? 'secondary' : 'destructive'
                    }>
                      {documents.governance_dashboard?.health_status}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    Overall Program Health: {documents.governance_dashboard?.overall_health_score}%
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Health Score */}
                  <div className="space-y-2">
                    <Progress value={documents.governance_dashboard?.overall_health_score || 0} className="h-3" />
                  </div>

                  {/* Metrics Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(documents.governance_dashboard?.metrics || {}).map(([key, metric]) => (
                      <Card key={key} className={`border-l-4 ${
                        metric.status === 'good' ? 'border-l-emerald-500' :
                        metric.status === 'warning' ? 'border-l-amber-500' : 'border-l-red-500'
                      }`}>
                        <CardContent className="pt-4">
                          <div className="text-sm text-slate-500">{metric.label}</div>
                          <div className="text-2xl font-bold mt-1">{metric.value}%</div>
                          <div className="text-xs text-slate-400 mt-1">{metric.detail}</div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Recommendations */}
                  {documents.governance_dashboard?.recommendations?.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-medium">Recommendations</h4>
                      {documents.governance_dashboard.recommendations.map((rec, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                          <Badge variant={rec.priority === 'HIGH' ? 'destructive' : 'secondary'}>
                            {rec.priority}
                          </Badge>
                          <div>
                            <p className="text-sm font-medium">{rec.area}</p>
                            <p className="text-sm text-slate-600">{rec.recommendation}</p>
                            <p className="text-xs text-slate-400 mt-1">Target: {rec.target}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleResetWizard}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Start Over
            </Button>
            <Button variant="outline" onClick={async () => {
              try {
                const token = getToken();
                const res = await fetch(`${API_URL}/api/bia-engine/policy/export/pdf`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                if (res.ok) {
                  const blob = await res.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'BCM_Policy.pdf';
                  a.click();
                  window.URL.revokeObjectURL(url);
                  toast.success('PDF exported!');
                } else {
                  toast.error('Failed to export PDF');
                }
              } catch (err) {
                toast.error('Failed to export PDF');
              }
            }} data-testid="export-pdf-btn">
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            {documents.status !== 'approved' && (
              <Button onClick={async () => {
                const token = getToken();
                const res = await fetch(`${API_URL}/api/bia-engine/policy/approve`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` }
                });
                if (res.ok) {
                  toast.success('Policy approved!');
                  await fetchDocuments();
                }
              }} data-testid="approve-policy-btn">
                <Check className="w-4 h-4 mr-2" />
                Approve Policy
              </Button>
            )}
          </div>
        </div>
      )}
      </div>
    </WizardShell>
  );
}
